import classNames from 'classnames';
import { IconEdit } from 'featherico';
import { every, filter, includes, map, reduce } from 'lodash';
import PropTypes from 'prop-types';
import { pure } from '../../../hoc/recompose';
import Button from '../../Button';
import { DeprecatedButtonContainer } from '../../ButtonContainer';
import Crossfade from '../../Crossfade';
import IconValidation from '../../IconValidation';
import { Shaking, useShaker } from '../../Shake';
import { ScrollStep, WizardScroller } from '../../Wizard/Scroller';
import styles from './style.m.less';

function CycleCreateContent(props) {
    const { actions, controls, content, currentStep, editedSteps, model, validationData, mode } = props;

    const editableContent = filter(content, item => !!item.components.edit);
    const editedStates = getEditedStates(editableContent, editedSteps);
    const nextButtons = getNextButtons(editableContent, editedSteps, model, actions, validationData, mode);

    const isValid = getIsValid(content, model, validationData, mode);
    const controlProps = {
        isValid: isValid && every(editedStates),
    };

    return (
        <WizardScroller currentStepKey={currentStep}>
            <section className="cycle-create-content cards_section">
                {map(content, (item, key) => {
                    const { id, canEdit } = item;
                    const editMode = currentStep === id;
                    const nextButton = nextButtons[id];
                    const wasEdited = editedStates[id];
                    const itemProps = {
                        model,
                        item,
                        actions,
                        nextButton,
                        wasEdited,
                        editMode,
                        validationData,
                        currentStep,
                        canEdit: (canEdit || (() => true))(),
                        mode,
                    };
                    return <ItemContainer key={key} {...itemProps} />;
                })}
                <controls.component {...controlProps} />
            </section>
        </WizardScroller>
    );
}

const ItemContainer = props => {
    const { editMode, wasEdited, item, currentStep, actions } = props;
    const { id, className } = item;
    const step = { key: id };

    const isUntouched = getIsUntouched(wasEdited, editMode);
    const isEmpty = !id;

    const containerClasses = classNames('cycle-create-content-item-container', {
        'cycle-create-content-item-untouched': isUntouched,
    });

    const mainClasses = classNames(
        'cycle-create-content-item-main',
        {
            'cycle-create-content-item-main-untouched': isUntouched,
            'cycle-create-content-item-main-inactive': !isUntouched && !editMode,
            'cycle-create-content-item-main-active': !isUntouched && editMode,
            'cycle-create-content-item-main-empty': isEmpty,
            card: !isUntouched,
        },
        className
    );

    const onEnterEdit = () => actions.enterEditMode(id);

    const shake = useShaker(currentStep);
    const body = () => {
        if (isUntouched) {
            return (
                <ScrollStep key="body-untouched" step={step}>
                    <ItemHeadline {...props} onClick={shake} />
                </ScrollStep>
            );
        }

        return editMode ? (
            <ScrollStep key="body-edit" step={step}>
                <ContentEditItem {...props} />
            </ScrollStep>
        ) : (
            <ScrollStep key="body-preview" step={step}>
                <ContentPreviewItem {...props} onEnterEdit={onEnterEdit} />
            </ScrollStep>
        );
    };

    return (
        <div className={containerClasses}>
            <div className={mainClasses}>
                <Crossfade>{body()}</Crossfade>
            </div>
        </div>
    );
};

function ContainerIcon({ wasEdited, isValid, isEditMode }) {
    if (!wasEdited || (!isEditMode && isValid)) {
        return null;
    }
    return (
        <span className="cycle-create-content-item-title-icon">
            <Crossfade>
                {isEditMode ? <IconEdit key="edit-icon" /> : <IconValidation key="validation-icon" isValid={isValid} />}
            </Crossfade>
        </span>
    );
}

const ItemTitle = pure(function ItemTitle({ children, active, isPreview, options }) {
    const props = {
        children,
        className: classNames(
            {
                active: active,
            },
            'card-section-heading'
        ),
    };
    return (
        <header className={isPreview ? 'cycle-create-content-item-title' : styles.sectionHeader}>
            <h1 {...props} />
            {options}
        </header>
    );
});

function ItemHeadline({ model, wasEdited, editMode, item, isUntouched, canEdit, onClick, validationData, mode }) {
    const { imperativeLabel, label } = item;
    const getLabel = () => {
        const text =
            editMode || isUntouched || !checkValidity(item, model, validationData, mode) ? imperativeLabel : label;
        if (onClick && canEdit) {
            return (
                <Button kind="PLAIN" className="card-header-heading-link" onClick={onClick}>
                    {text}
                </Button>
            );
        } else {
            return text;
        }
    };
    const isPreview = wasEdited && !editMode;
    return (
        <ItemTitle
            isPreview={isPreview}
            options={editMode && item.components.editOptions && <item.components.editOptions />}
        >
            <ContainerIcon
                wasEdited={wasEdited}
                isValid={checkValidity(item, model, validationData, mode)}
                isEditMode={editMode}
            />
            {editMode ? ' ' : null}
            {getLabel()}
        </ItemTitle>
    );
}

function ContentEditItem(props) {
    const { item, nextButton, wasEdited } = props;
    const { onClick } = nextButton;
    const { components, id, stickyButtons } = item;
    return (
        <div>
            <ItemHeadline {...props} />
            <div className="cycle-create-content-item-edit">
                <components.edit wasEdited={wasEdited} />
                <div className={classNames({ [styles.stickyButton]: stickyButtons })}>
                    <Shaking shakeKey={id}>
                        <DeprecatedButtonContainer>
                            {components.skip ? <components.skip onSkip={onClick} /> : null}
                            {components.next ? <components.next {...nextButton} /> : <NextButton {...nextButton} />}
                        </DeprecatedButtonContainer>
                    </Shaking>
                </div>
            </div>
        </div>
    );
}

function ContentPreviewItem(props) {
    const { item, canEdit } = props;
    const { components } = item;
    return (
        <div className="cycle-create-content-item-preview">
            <div className="cycle-create-content-item-preview-title">
                <ItemHeadline {...props} onClick={props.onEnterEdit} />
            </div>
            <div className="cycle-create-content-item-preview-content">
                <components.preview />
            </div>
            <Button
                className="cycle-create-content-item-preview-edit-button"
                onClick={props.onEnterEdit}
                disabled={!canEdit}
            >
                <IconEdit /> Edit
            </Button>
        </div>
    );
}

function NextButton({ label, ...other }) {
    return (
        <Button kind="PRIMARY" {...other}>
            {label}
        </Button>
    );
}

function getNextButtons(content, editedSteps, model, { next, leaveEditMode }, validationData, mode) {
    const allEdited = wereAllStepsEdited(content, editedSteps);
    return reduce(
        content,
        (mem, item) => {
            const itemEdited = wasItemEdited(item, editedSteps);

            const label = itemEdited ? 'Done' : 'Next';
            const onClick = allEdited ? leaveEditMode : next;

            mem[item.id] = {
                label,
                onClick,
                disabled: !checkNextButtonValidity(item, model, validationData, mode),
            };
            return mem;
        },
        {}
    );
}

function wereAllStepsEdited(content, editedSteps) {
    return every(content, contentItem => wasItemEdited(contentItem, editedSteps));
}

function wasItemEdited(item, editedSteps) {
    return editedSteps.indexOf(item.id) !== -1;
}

function checkNextButtonValidity(item, model, validationData, mode) {
    const { isNextEnabled } = item.components.edit;
    if (isNextEnabled) {
        return isNextEnabled(model);
    } else {
        return checkValidity(item, model, validationData, mode);
    }
}

function checkValidity(item, model, validationData, mode) {
    return (item.components.edit.isValid || (() => true))(model, validationData, mode);
}

function checkValidityWithTimeline(item, model, validationData, mode) {
    const editComponentValidFn = (item.components.edit && item.components.edit.isValid) || (() => true);
    const timelineComponentValidFn = (item.components.timeline && item.components.timeline.isValid) || (() => true);
    return editComponentValidFn(model, validationData, mode) && timelineComponentValidFn(model);
}

function getIsUntouched(wasEdited, editMode) {
    return !wasEdited && !editMode;
}

function getEditedStates(content, editedSteps) {
    return reduce(
        content,
        (mem, item) => {
            mem[item.id] = includes(editedSteps, item.id);
            return mem;
        },
        {}
    );
}

function getIsValid(content, model, validationData, mode) {
    return every(content, item => checkValidityWithTimeline(item, model, validationData, mode));
}

CycleCreateContent.propTypes = {
    controls: PropTypes.shape({
        component: PropTypes.elementType,
    }),
    content: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.shape({
                preview: PropTypes.elementType,
                edit: PropTypes.elementType,
            }),
        })
    ).isRequired,
    currentStep: PropTypes.string,
    actions: PropTypes.shape({
        next: PropTypes.func,
        enterEditMode: PropTypes.func,
        leaveEditMode: PropTypes.func,
    }),
};

export { CycleCreateContent };
